import { useEffect, useRef, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { scheduleGapiTransportRefresh } from '../config/gapiReconnect';
import { pingServer } from '../services/time/serverConnectivity';

export interface NetworkStatus {
  /** Browser / device reports connectivity */
  networkOnline: boolean;
  /** Backend health check succeeded recently (sticky; tolerates brief ping flaps) */
  serverReachable: boolean;
  /** Dev-only: force offline simulation (DevPanel) */
  simulateOffline: boolean;
  /** Effective online state for writes / queue flush */
  online: boolean;
  setSimulateOffline: (value: boolean) => void;
}

const SERVER_PING_MS = 30_000;
/** Require this many consecutive failed pings before marking unreachable. */
const PING_FAIL_THRESHOLD = 2;

export function useNetworkStatus(): NetworkStatus {
  const [networkOnline, setNetworkOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [serverReachable, setServerReachable] = useState(DATA_ADAPTER !== 'firebase');
  const [simulateOffline, setSimulateOffline] = useState(false);
  const failStreakRef = useRef(0);

  useEffect(() => {
    const onOnline = () => {
      setNetworkOnline(true);
      scheduleGapiTransportRefresh();
    };
    const onOffline = () => setNetworkOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const attachCapacitor = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { Network } = await import('@capacitor/network');
        const status = await Network.getStatus();
        if (!cancelled) setNetworkOnline(status.connected);
        const handle = await Network.addListener('networkStatusChange', (s) => {
          if (!cancelled) setNetworkOnline(s.connected);
          if (s.connected) scheduleGapiTransportRefresh();
        });
        return () => {
          handle.remove();
        };
      } catch {
        return undefined;
      }
    };
    const cleanup = attachCapacitor();
    return () => {
      cancelled = true;
      cleanup.then((fn) => fn?.());
    };
  }, []);

  useEffect(() => {
    if (!networkOnline) {
      failStreakRef.current = PING_FAIL_THRESHOLD;
      setServerReachable(false);
      return undefined;
    }

    let cancelled = false;
    const runPing = async () => {
      const ok = await pingServer();
      if (cancelled) return;
      if (ok) {
        failStreakRef.current = 0;
        setServerReachable(true);
        return;
      }
      failStreakRef.current += 1;
      if (failStreakRef.current >= PING_FAIL_THRESHOLD) {
        setServerReachable(false);
      }
      // Stay reachable after a single flake so Firestore cache / UI keep working.
    };

    runPing();
    const id = window.setInterval(runPing, SERVER_PING_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [networkOnline]);

  const online = networkOnline && serverReachable && !simulateOffline;

  return { networkOnline, serverReachable, simulateOffline, online, setSimulateOffline };
}
